import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Award, Star, Share2, Calendar } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import Logo3D from "@/components/Logo3D";
import { supabase } from "@/integrations/supabase/client";

const categoryIcons: Record<string, typeof Award> = {
  "Awards": Award,
  "Client Testimonial": Star,
  "Social Media": Share2,
  "News": Calendar,
  "Updates": Calendar,
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { data: blogPosts = [], isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data.map((p) => ({
        title: p.title,
        category: p.category,
        date: p.date || "",
        image: p.image_url || "",
        excerpt: p.excerpt || "",
      }));
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  // Realtime: refresh when admin creates/updates/deletes posts
  useEffect(() => {
    const channel = supabase
      .channel("blog_posts_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "blog_posts" },
        () => queryClient.invalidateQueries({ queryKey: ["blog-posts"] })
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const filteredPosts = activeCategory
    ? blogPosts.filter((p) => p.category === activeCategory)
    : blogPosts;

  const blogJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Dhivisha Architects Blog",
    "description": "Architecture news, awards, and client stories from Dhivisha Architects, COA-registered firm in Kumbakonam.",
    "url": "https://dhivishaarchitects.com/blog",
    "publisher": { "@id": "https://dhivishaarchitects.com/#organization" },
    "blogPost": blogPosts.map((p) => ({
      "@type": "BlogPosting",
      "headline": p.title,
      "description": p.excerpt,
      "datePublished": p.date,
      "image": p.image,
      "author": { "@id": "https://dhivishaarchitects.com/#organization" },
    })),
  };

  return (
    <Layout transparentNav>
      <SEOHead
        title="Blog — Architectural Insights from Kumbakonam & Delta District"
        description="Latest from Dhivisha Architects, registered firm in Kumbakonam: awards, client stories, featured projects, design insights & architectural trends from the Cauvery Delta region."
        path="/blog"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogJsonLd) }}
      />
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=1200&q=70" alt="Blog" className="w-full h-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
          <div className="absolute inset-0 bg-secondary/70" />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 text-center px-4">
          <Logo3D size="lg" animate glow className="mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">Our Blog</h1>
          <p className="text-lg text-primary-foreground/80">Our regular updates — awards, client testimonials, and social media promotion</p>
        </motion.div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 bg-muted">
        <motion.div
          className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center gap-3"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <button
            onClick={() => setActiveCategory(null)}
            className={`flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition-all ${
              activeCategory === null
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:border-primary hover:text-primary"
            }`}
          >
            <span>All</span>
          </button>
          {["Awards", "Client Testimonial", "Social Media"].map((cat) => {
            const Icon = categoryIcons[cat];
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(isActive ? null : cat)}
                className={`flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition-all ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:border-primary hover:text-primary"
                }`}
              >
                <Icon size={16} />
                <span>{cat}</span>
              </button>
            );
          })}
        </motion.div>
      </section>

      {/* Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {filteredPosts.length === 0 && !isLoading && (
            <p className="text-center text-muted-foreground">No posts in this category.</p>
          )}
          <motion.div
            key={activeCategory || "all"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {filteredPosts.map((post) => {
              const Icon = categoryIcons[post.category] || Calendar;
              return (
                <motion.article
                  key={post.title}
                  variants={staggerItem}
                  whileHover={{ y: -4 }}
                  className="bg-card border border-border rounded overflow-hidden hover:shadow-lg hover:border-primary transition-all group"
                >
                  <div className="h-52 overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon size={14} className="text-primary" />
                      <span className="text-xs uppercase tracking-widest text-primary font-semibold">{post.category}</span>
                      <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1"><Calendar size={12} /> {post.date}</span>
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">{post.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{post.excerpt}</p>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>
      {/* Sign Up for Alerts */}
      <SignUpAlerts />
    </Layout>
  );
};

const SignUpAlerts = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || !trimmedEmail) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    toast({ title: "Subscribed!", description: "You'll receive our latest updates." });
    setName("");
    setEmail("");
  };

  return (
    <motion.section
      className="py-10 border-t border-border"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">Sign up for alerts</h2>
          <p className="text-sm text-muted-foreground">Register to receive our latest updates</p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-stretch gap-3 w-full md:w-auto">
          <Input placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} className="sm:w-56 bg-background" maxLength={100} required />
          <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="sm:w-56 bg-background" maxLength={255} required />
          <Button type="submit" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold px-8 active:scale-95 transition-transform">Submit</Button>
        </form>
      </div>
    </motion.section>
  );
};

export default Blog;
